import { proxy } from "valtio";
import { buildHandoffBeats } from "./buildHandoffBeats";
import {
  trackHandoffActivateTap,
  trackHandoffBeatAdvance,
  trackHandoffBeatView,
  trackHandoffComplete,
  trackHandoffSkip,
  trackHandoffStart,
} from "./handoffAnalytics";
import type {
  HandoffBeatId,
  HandoffBranch,
  HandoffSessionState,
  HandoffVariant,
} from "./handoffTypes";

const initialState = (): HandoffSessionState => ({
  active: false,
  beats: [],
  beatIndex: 0,
  hasGoal: false,
  reducedMotion: false,
  beatsResolved: false,
  variant: "unactivated",
  branch: "B",
});

export const handoffSessionStore = proxy<HandoffSessionState>(initialState());

function readReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) {
    return false;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function isHandoffSessionActive(): boolean {
  return handoffSessionStore.active;
}

export function getHandoffCurrentBeat(): HandoffBeatId | null {
  if (!handoffSessionStore.active) {
    return null;
  }
  return handoffSessionStore.beats[handoffSessionStore.beatIndex] ?? null;
}

/** Begin session; beats may be refined via resolveHandoffBeats after Plan mounts. */
export function startHandoffSession(options?: {
  hasGoal?: boolean;
  variant?: HandoffVariant;
  branch?: HandoffBranch;
}): void {
  if (handoffSessionStore.active) {
    return;
  }

  const hasGoal = options?.hasGoal ?? true;
  const variant = options?.variant ?? "unactivated";
  const branch = options?.branch ?? "B";
  const reducedMotion = readReducedMotion();
  const beats = buildHandoffBeats(hasGoal);

  handoffSessionStore.active = true;
  handoffSessionStore.hasGoal = hasGoal;
  handoffSessionStore.variant = variant;
  handoffSessionStore.branch = branch;
  handoffSessionStore.reducedMotion = reducedMotion;
  handoffSessionStore.beats = beats;
  handoffSessionStore.beatIndex = 0;
  handoffSessionStore.beatsResolved = options?.hasGoal != null;

  trackHandoffStart({
    has_goal: hasGoal,
    reduced_motion: reducedMotion,
    variant,
    branch,
  });

  const first = beats[0];
  if (first) {
    trackHandoffBeatView({ beat_id: first, beat_index: 0 });
  }
}

/**
 * After Plan DOM is ready: shrink to 2 beats if no parked goal anchor.
 * Only runs once per session when hasGoal was speculative.
 */
export function resolveHandoffBeats(hasGoal: boolean): void {
  if (!handoffSessionStore.active || handoffSessionStore.beatsResolved) {
    return;
  }

  handoffSessionStore.beatsResolved = true;
  if (hasGoal === handoffSessionStore.hasGoal) {
    return;
  }

  handoffSessionStore.hasGoal = hasGoal;
  handoffSessionStore.beats = buildHandoffBeats(hasGoal);
  handoffSessionStore.beatIndex = 0;

  const first = handoffSessionStore.beats[0];
  if (first) {
    trackHandoffBeatView({ beat_id: first, beat_index: 0 });
  }
}

export function advanceHandoffBeat(): void {
  if (!handoffSessionStore.active) {
    return;
  }
  const current = getHandoffCurrentBeat();
  if (!current) {
    return;
  }

  const nextIndex = handoffSessionStore.beatIndex + 1;
  if (nextIndex >= handoffSessionStore.beats.length) {
    return;
  }

  trackHandoffBeatAdvance({ beat_id: current });
  handoffSessionStore.beatIndex = nextIndex;

  const next = handoffSessionStore.beats[nextIndex];
  if (next) {
    trackHandoffBeatView({ beat_id: next, beat_index: nextIndex });
  }
}

export function endHandoffSession(
  reason: "skip" | "go_brief" | "activate" | "teardown",
): void {
  if (!handoffSessionStore.active) {
    return;
  }

  const beat = getHandoffCurrentBeat();
  const atBeat = handoffSessionStore.beatIndex + 1;

  if (reason === "skip" && beat) {
    trackHandoffSkip({ beat_id: beat, at_beat: atBeat });
  } else if (reason === "go_brief") {
    trackHandoffComplete();
  } else if (reason === "activate") {
    trackHandoffActivateTap();
  }

  Object.assign(handoffSessionStore, initialState());
}

/** Reset without analytics — tests / kill switch. */
export function resetHandoffSessionStore(): void {
  Object.assign(handoffSessionStore, initialState());
}
